import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useLang } from '../../context/LangContext';
import { useUserAuth } from '../../context/UserAuthContext';
import CenteredLayout from '../../layout/CenteredLayout';
import MainLayout from '../../layout/MainLayout';
import ResponsiveView from '../../layout/ResponsiveView';
import ProjectAppbar from '../../customComponents/ProjectAppbar';
import { FormTitle } from '../../customComponents/FormTitle';
import YouTubePlayer from '../../components/YouTubePlayer';
import AuctionInfo from '../../components/AuctionInfo';
import MyBid from '../../components/MyBid';
import BidHistory from '../../components/BidHistory';
import PrimaryButton from '../../components/PrimaryButton';
import BottomFixedAction from '../../components/BottomFixedAction';
import AnimatedBorderButton, { AnimatedBorderButtonRef } from '../../components/AnimatedBorderButton';
import AuctionTimer from '../../components/AuctionTimer';
import ImageCarousel from '../../components/ImageCarousel';
import LotDescription from '../../components/LotDescription';
import { useAuctionSocket } from '../../hooks/useAuctionSocket';
import { BiddingRange } from '../../types/socket.types';
import { devWarn, devLog } from '../../utils/devLogger';
import LoginRequiredModal from '../../components/LoginRequiredModal';
import AdditionalInfoInputModal from '../../components/AdditionalInfoInputModal';
import BidRestrictedModal from '../../components/BidRestrictedModal';
import AuctionEndedModal from '../../components/AuctionEndedModal';
import { toast } from 'react-toastify';
import { getAgreeAuction } from '../../lib/api/user/userApi';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-bottom: 140px; /* 고정 버튼 영역을 위한 하단 패딩 증가 */
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 16px 0;
  gap: 0; /* 각 컴포넌트에서 margin-bottom으로 간격 조절 */
`;

// TitleRow 스타일드 컴포넌트 (LotDetail과 동일)
const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 24px;
`;

const TitleText = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

// 라이브참여하기 버튼 (LotDetail과 동일) - React.memo로 최적화
const LiveButton = styled.button`
  width: auto;
  min-width: 120px;
  padding: 0 12px;
  box-sizing: border-box;
  height: 28px;
  background-color: #a6d4d1;
  border: none;
  border-radius: 8px;
  color: #262626;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`;

// 최적화된 라이브 버튼 컴포넌트
const OptimizedLiveButton = React.memo(({ onClick, children }: { onClick: () => void; children: React.ReactNode }) => {
  return (
    <LiveButton onClick={onClick}>
      {children}
    </LiveButton>
  );
});

// TitleRow 컴포넌트를 메모이제이션
const MemoizedTitleRow = React.memo(({ 
  title, 
  onWatchLive, 
  watchLiveText 
}: { 
  title: string; 
  onWatchLive: () => void; 
  watchLiveText: string; 
}) => {
  return (
    <TitleRow>
      <TitleText>{title}</TitleText>
      <OptimizedLiveButton onClick={onWatchLive}>
        {watchLiveText}
      </OptimizedLiveButton>
    </TitleRow>
  );
});

// 고정 버튼 영역을 메모이제이션 - 소켓 상태와 독립적
const MemoizedFixedButtonContainer = React.memo(({ 
  onClick,
  buttonText,
  isAgreed,
  isSubmitting,
  isHighestBidder,
  tooltipText,
  waitingTooltipText,
  animatedButtonRef,
  onStartPress,
  onIncomplete,
  disabledCallback
}: { 
  onClick: () => void;
  buttonText: string;
  isAgreed: boolean | null;
  isSubmitting: boolean;
  isHighestBidder: boolean;
  tooltipText: string;
  waitingTooltipText: string;
  animatedButtonRef: React.RefObject<AnimatedBorderButtonRef | null>;
  onStartPress: () => void;
  onIncomplete: () => void;
  disabledCallback: () => void;
}) => {
  if (isAgreed === true) {
    return (
      <BottomFixedAction showBorderTop align="center">
        <AnimatedBorderButton
          ref={animatedButtonRef}
          title={isSubmitting ? '입찰 진행 중...' : buttonText}
          tooltipText={isHighestBidder ? waitingTooltipText : (isSubmitting ? '입찰이 진행 중입니다' : tooltipText)}
          onStartPress={onStartPress}
          onPressed={onClick}
          onIncomplete={onIncomplete}
          disabledCallback={disabledCallback}
          duration={1}
          height={48}
          borderRadius={16}
          borderThickness={3}
          backgroundColor="#A6D4D1"
          finalBorderColor="#22c55e"
          textColor="#262626"
          textPressedColor="#FFFFFF"
          textCompletedColor="#FFFFFF"
          disabled={isSubmitting || isHighestBidder}
          showTooltipWhenDisabled={isHighestBidder}
        />
      </BottomFixedAction>
    );
  }
  return (
    <BottomFixedAction maxWidth={390} align="right">
      <PrimaryButton onClick={onClick}>
        {buttonText}
      </PrimaryButton>
    </BottomFixedAction>
  );
});

// LOT 상세 섹션 헤더
const LotDetailHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  margin-top: 24px;
  cursor: pointer;
`;

// 더보기/접기 버튼
const ToggleButton = styled.button`
  background: none;
  border: none;
  font-size: 14px;
  color: #666;
  cursor: pointer;
  padding: 4px 8px;
  
  &:hover {
    color: #333;
  }
`;

// 접을 수 있는 콘텐츠 컨테이너
const CollapsibleContent = styled.div<{ $isCollapsed: boolean }>`
  display: ${props => props.$isCollapsed ? 'none' : 'block'};
  margin-bottom: ${props => props.$isCollapsed ? '0' : '24px'};
  overflow: hidden;
  transition: all 0.3s ease;
`;

// 웹용 LOT 상세 컨텐츠 (좌우 배치)
const DesktopLotContent = styled.div`
  display: flex;
  gap: 24px;
  width: 100%;
  
  > * {
    flex: 1;
    min-width: 0; /* flex 컨테이너에서 overflow 방지 */
    max-width: 50%; /* 각각 최대 50%로 제한 */
  }
`;

// 이미지 캐로셀 래퍼 (웹용)
const ImageCarouselWrapper = styled.div`
  width: 100%;
  max-width: 100%;
  overflow: hidden;
`;

// 웹용 LOT 상세 타이틀
const DesktopLotTitle = styled.div`
  font-size: 18px;
  font-weight: bold;
  color: #000;
  text-align: left;
  margin: 0 0 16px 0;
`;

// 웹용 입찰 섹션 컨테이너 (나의 입찰 + 입찰 내역)
const DesktopBidSection = styled.div`
  display: flex;
  gap: 40px;
  width: 100%;
`;

// 나의 입찰 래퍼 (고정 너비 + 웹에서 배경색 + 고정 높이)
// 경매 타이머 래퍼 (웹용)
const AuctionTimerWrapper = styled.div`
  width: 100%;
  
  @media (min-width: 768px) {
    max-width: 1200px;
    margin: 0 auto;
  }
`;

const MyBidWrapper = styled.div<{ $hasBid: boolean }>`
  width: 350px;
  flex-shrink: 0;
  
  @media (min-width: 768px) {
    background: ${props => props.$hasBid ? '#f8f5f0' : '#f3f6f6'};
    border-radius: 12px;
    padding: 20px;
    height: 120px; /* 웹에서 고정 높이 설정 */
  }
`;

// 입찰 내역 래퍼 (나머지 공간)
const BidHistoryWrapper = styled.div`
  flex: 1;
  min-width: 0;
`;

// 에러 메시지 컴포넌트
const ErrorMessage = styled.div`
  background-color: #ffebee;
  color: #c62828;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

// 로딩 상태 컴포넌트
const LoadingMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  background-color: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 16px;
  color: #666;
  
  &::before {
    content: '';
    width: 20px;
    height: 20px;
    border: 2px solid #ddd;
    border-top: 2px solid #666;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-right: 12px;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const liveBidTexts = {
  ko: {
    title: '라이브 입찰',
    bidButton: '경매등록하기',
    bidButtonTooltip: '1초간 꾹 눌러서 입찰',
    waitingNextBidderTooltip: '다음 경쟁자를 기다리고 있어요',
    bid: '입찰', // 입찰 텍스트 추가
    agreementStatusLoading: '동의 여부를 확인하는 중입니다. 잠시 후 다시 시도해주세요.',
    socketRequired: '소켓 연결이 필요합니다. 잠시 후 다시 시도해주세요.',
    auctionLoading: '경매 정보를 불러오는 중입니다. 잠시 후 다시 시도해주세요.',
    currentLotMissing: '현재 진행 중인 로트 정보를 찾을 수 없습니다.',
    invalidBidPrice: '입찰가가 올바르지 않습니다.',
    bidInProgress: '입찰이 이미 진행 중입니다.',
    bidSubmitting: '입찰을 진행하고 있습니다...',
    liveAuction: '라이브경매',
    watchLive: '라이브참여하기',
    showMore: '더보기', // 더보기 텍스트 추가
    showLess: '접기', // 접기 텍스트 추가
    lotDetail: 'LOT 상세', // LOT 상세 텍스트 추가
    connected: '라이브 연결됨',
    connecting: '연결 중...',
    disconnected: '연결 끊김',
    noAuctionData: '경매 정보를 불러오는 중...',
    retry: '다시 시도',
    missingParams: '경매 정보가 누락되었습니다. 다시 시도해주세요.',
    goBack: '이전으로'
  },
  en: {
    title: 'Live Bid',
    bidButton: 'Live Bid',
    bidButtonTooltip: 'Hold for 1 second to bid',
    waitingNextBidderTooltip: 'Waiting for the next competitor',
    bid: 'Bid', // 입찰 텍스트 추가
    agreementStatusLoading: 'Checking agreement status. Please try again shortly.',
    socketRequired: 'Socket connection is required. Please try again shortly.',
    auctionLoading: 'Auction data is still loading. Please try again shortly.',
    currentLotMissing: 'Unable to find the current lot.',
    invalidBidPrice: 'Bid price is invalid.',
    bidInProgress: 'A bid is already in progress.',
    bidSubmitting: 'Bidding in progress...',
    liveAuction: 'Live Auction',
    watchLive: 'Watch Live',
    showMore: 'Show More', // 더보기 텍스트 추가
    showLess: 'Show Less', // 접기 텍스트 추가
    lotDetail: 'LOT Details', // LOT 상세 텍스트 추가
    connected: 'Live Connected',
    connecting: 'Connecting...',
    disconnected: 'Disconnected',
    noAuctionData: 'Loading auction data...',
    retry: 'Retry',
    missingParams: 'Auction information is missing. Please try again.',
    goBack: 'Go Back'
  }
};

function LiveBid() {
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useLang();
  const { user } = useUserAuth();
  const texts = liveBidTexts[lang];
  const [isLotDetailCollapsed, setIsLotDetailCollapsed] = useState(false);
  const [showAuctionEndModal, setShowAuctionEndModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showBidRestrictedModal, setShowBidRestrictedModal] = useState(false);
  const [showAdditionalInfoInputModal, setShowAdditionalInfoInputModal] = useState(false);
  const [isAuctionAgreed, setIsAuctionAgreed] = useState<boolean | null>(null);
  const [isAgreementStatusLoading, setIsAgreementStatusLoading] = useState(false);
  const [isSubmittingBid, setIsSubmittingBid] = useState(false);
  const [bidSubmitTime, setBidSubmitTime] = useState<number | null>(null);
  const [lastBidRequestId, setLastBidRequestId] = useState<string | null>(null);

  // LiveBid에서는 소켓 데이터(auctionData.lots)의 likedUserIndexes가 즉시 반영되지 않을 수 있어
  // 로트별로 로컬에서 낙관적(optimistic) 업데이트를 유지한다.
  const [likedUserIndexesByLot, setLikedUserIndexesByLot] = useState<Record<number, number[]>>({});

  // 소켓 상태를 ref로 저장하여 리렌더링과 독립적으로 관리
  const socketStateRef = useRef({
    isConnected: false,
    auctionData: null as any
  });

  // 화면에 마지막으로 표시된 lot index를 기억 (경매 종료 후 유지용)
  const lastActiveLotIndexRef = useRef<number | null>(null);
  // 모든 lot 종료 후에는 lot 변경을 동결
  const freezeLotAfterAuctionEndRef = useRef(false);

  // location.state로부터 파라미터 추출
  const { auctionIndex: paramAuctionIndex, lotIndex: paramLotIndex, selectedItem } = location.state || {};

  // selectedItem 구조 분석
  const auctionIndex = paramAuctionIndex || 
                      selectedItem?.apiData?.auction?.index || 
                      selectedItem?.apiData?.auction?.id ||
                      selectedItem?.auctionIndex || 
                      selectedItem?.auctionId;
                      
  const lotIndex = paramLotIndex || 
                  selectedItem?.apiData?.index || 
                  selectedItem?.id ||
                  selectedItem?.lotIndex || 
                  selectedItem?.lotId;

  const routeLotIndex = useMemo(() => {
    if (lotIndex === undefined || lotIndex === null) return null;
    const asNumber = Number(lotIndex);
    return Number.isFinite(asNumber) ? asNumber : null;
  }, [lotIndex]);
  
  // 소켓 훅 사용 - 자동 데이터 요청 비활성화하여 중복 호출 방지
  const {
    isConnected,
    auctionData,
    bidHistory,
    countdownStatus, // 카운트다운 상태 추가
    currentLot, // 소켓 이벤트로 관리되는 현재 진행 중인 lot
    isAuctionEnded, // 소켓 이벤트로 관리되는 경매 종료 상태
    isBanned, // 사용자 차단 상태
    // error,
    getAuctionInfo,
    getBidHistory,
    placeBid,
    // clearError,
    // clearBidHistory
  } = useAuctionSocket({
    auctionIndex,
    autoConnect: true,
    autoRequestData: false // 자동 데이터 요청 비활성화 - 컴포넌트에서 직접 제어
  });

  // 현재 표시할 lot을 가져오는 유틸리티 함수 - 소켓 이벤트 우선
  const getCurrentLot = useCallback((lots: any[]) => {
    // 경매 종료(auction-ended) 이후에는 마지막으로 표시되던 lot를 그대로 유지
    if ((isAuctionEnded || freezeLotAfterAuctionEndRef.current) && lastActiveLotIndexRef.current != null) {
      const frozenLot = lots.find(lot => lot.index === lastActiveLotIndexRef.current);
      if (frozenLot) {
        devLog('🎯 [LiveBid] 경매 종료 후 마지막 lot 유지:', {
          lotIndex: frozenLot.index,
          lotNo: frozenLot.lotNo
        });
        return frozenLot;
      }
    }

    // 1. 소켓 이벤트로 받은 currentLot이 있으면 우선 사용
    if (currentLot?.lotIndex != null) {
      const socketLot = lots.find(lot => lot.index === currentLot.lotIndex);
      if (socketLot) {
        // 진행 중 상태에서만 마지막 lot index 업데이트
        if (!isAuctionEnded && !freezeLotAfterAuctionEndRef.current) {
          lastActiveLotIndexRef.current = socketLot.index;
        }
        devLog('🎯 [LiveBid] 소켓 이벤트로 받은 현재 lot 사용:', {
          lotIndex: currentLot.lotIndex,
          lotNo: currentLot.lotNo,
          lotName: currentLot.lotName
        });
        return socketLot;
      }
    }

    // 2) currentLot 이벤트가 누락되는 환경이 있어, 소켓 카운트다운/타이머의 lotIndex를 차선으로 사용
    if (countdownStatus?.lotIndex != null) {
      const countdownLot = lots.find(lot => lot.index === countdownStatus.lotIndex);
      if (countdownLot) {
        if (!isAuctionEnded && !freezeLotAfterAuctionEndRef.current) {
          lastActiveLotIndexRef.current = countdownLot.index;
        }
        devLog('🎯 [LiveBid] 소켓 countdownStatus.lotIndex로 현재 lot 사용:', {
          lotIndex: countdownStatus.lotIndex
        });
        return countdownLot;
      }
    }

    // 3) 마지막 보완: 라우팅으로 명시적으로 전달받은 lotIndex (추정 로직 아님)
    if (routeLotIndex != null) {
      const routeLot = lots.find(lot => lot.index === routeLotIndex);
      if (routeLot) {
        devLog('🎯 [LiveBid] 소켓 currentLot 미확인 - 라우팅 lotIndex로 표시:', {
          lotIndex: routeLotIndex
        });
        return routeLot;
      }
    }

    return null;
  }, [isAuctionEnded, currentLot?.lotIndex, currentLot?.lotNo, currentLot?.lotName, countdownStatus?.lotIndex, routeLotIndex]);

  // getAuctionInfo, getBidHistory 함수를 ref로 저장하여 최신 함수 참조 유지
  const getAuctionInfoRef = useRef(getAuctionInfo);
  getAuctionInfoRef.current = getAuctionInfo;
  
  const getBidHistoryRef = useRef(getBidHistory);
  getBidHistoryRef.current = getBidHistory;



  // 컴포넌트 마운트 시 경매 정보 요청 - 중복 호출 방지를 위해 ref 사용
  const hasRequestedAuctionInfo = useRef(false);
  const lastRequestedAuctionIndex = useRef<number | null>(null);
  const lastRequestedAgreementAuctionIndex = useRef<number | null>(null);
  const bidInProgressRef = useRef(false);
  const bidTargetLotIndexRef = useRef<number | null>(null);
  const lastRequestedPriceRef = useRef<number | null>(null);
  const lastBidWarningAtRef = useRef<number | null>(null);
  const animatedButtonRef = useRef<AnimatedBorderButtonRef>(null);
  
  useEffect(() => {
    if (isConnected && auctionIndex) {
      // 이미 같은 auctionIndex로 요청했으면 스킵
      if (hasRequestedAuctionInfo.current && lastRequestedAuctionIndex.current === auctionIndex) {
        devLog('🏛️ [LiveBid] 경매 정보 요청 스킵 (이미 요청함):', {
          auctionIndex,
          lastRequestedAuctionIndex: lastRequestedAuctionIndex.current
        });
        return;
      }
      
      devLog('🏛️ [LiveBid] 경매 정보 요청 (최초 또는 auctionIndex 변경):', {
        auctionIndex,
        lastRequestedAuctionIndex: lastRequestedAuctionIndex.current
      });
      
      hasRequestedAuctionInfo.current = true;
      lastRequestedAuctionIndex.current = auctionIndex;
      getAuctionInfoRef.current(auctionIndex);
    }
    
    // 연결이 끊어지면 플래그 리셋
    if (!isConnected) {
      hasRequestedAuctionInfo.current = false;
      lastRequestedAuctionIndex.current = null;
    }
  }, [isConnected, auctionIndex]); // getAuctionInfo 의존성 제거

  useEffect(() => {
    if (!auctionIndex) {
      lastRequestedAgreementAuctionIndex.current = null;
      setIsAuctionAgreed(null);
      setIsAgreementStatusLoading(false);
      return;
    }

    if (lastRequestedAgreementAuctionIndex.current === auctionIndex) {
      return;
    }

    lastRequestedAgreementAuctionIndex.current = auctionIndex;

    const fetchAgreementStatus = async () => {
      try {
        setIsAgreementStatusLoading(true);
        const response = await getAgreeAuction(auctionIndex);
        setIsAuctionAgreed(response.data.data?.agreed === true);
      } catch (error) {
        devWarn('⚠️ [LiveBid] 경매 동의 여부 조회 실패:', error);
        setIsAuctionAgreed(false);
      } finally {
        setIsAgreementStatusLoading(false);
      }
    };

    fetchAgreementStatus();
  }, [auctionIndex]);

  // 현재 표시되는 lot을 추적하여 lot 변경시 새로운 입찰 내역 로드
  const hasRequestedBidHistory = useRef(false);
  const lastRequestedLotIndex = useRef<number | null>(null);
  const currentDisplayedLotIndex = useRef<number | null>(null);
  
  // 현재 표시할 lot의 index를 계산하는 메모이제이션
  const currentLotIndex = useMemo(() => {
    // 소켓 이벤트로 받은 currentLot이 있으면 우선 사용
    if (currentLot?.lotIndex != null) {
      devLog('🎯 [LiveBid] 소켓 이벤트 currentLot 사용:', {
        lotIndex: currentLot.lotIndex,
        lotNo: currentLot.lotNo,
        lotName: currentLot.lotName
      });
      return currentLot.lotIndex;
    }

    if (countdownStatus?.lotIndex != null) {
      return countdownStatus.lotIndex;
    }

    // 경매 종료 이후에는 "마지막으로 소켓에서 확인한 lot"만 유지
    if ((isAuctionEnded || freezeLotAfterAuctionEndRef.current) && lastActiveLotIndexRef.current != null) {
      return lastActiveLotIndexRef.current;
    }

    if (routeLotIndex != null) {
      return routeLotIndex;
    }

    return null;
  }, [currentLot?.lotIndex, currentLot?.lotNo, currentLot?.lotName, countdownStatus?.lotIndex, isAuctionEnded, routeLotIndex]);

  // 모든 lot이 종료되었는지 확인하는 메모이제이션 - 소켓 이벤트 기반
  const allLotsEnded = useMemo(() => {
    // 소켓 이벤트로 받은 경매 종료 상태 우선 사용
    if (isAuctionEnded) {
      devLog('🔍 [LiveBid] 소켓 이벤트로 경매 종료 확인됨');
      return true;
    }
    
    // 기존 로직은 fallback으로 유지
    if (!auctionData?.lots || auctionData.lots.length === 0) return false;
    
    // disabled가 false인 lot 중에서 ended가 false인 lot이 있는지 확인
    const enabledLots = auctionData.lots.filter(lot => !lot.disabled);
    const activeLotsCount = enabledLots.filter(lot => !lot.ended).length;
    
    const allEnded = activeLotsCount === 0 && enabledLots.length > 0;
    
    devLog('🔍 [LiveBid] fallback 경매 종료 상태 확인:', {
      전체로트수: auctionData.lots.length,
      활성로트수: enabledLots.length,
      진행중로트수: activeLotsCount,
      모든로트종료: allEnded
    });
    
    return allEnded;
  }, [auctionData?.lots, isAuctionEnded]); // isAuctionEnded 의존성 추가

  // 모달 관련 핸들러들
  const handleCloseLoginModal = () => {
    setShowLoginModal(false);
  };

  const handleLoginFromModal = () => {
    setShowLoginModal(false);
    navigate('/login');
  };

  // 좋아요 기능을 위한 추가정보 모달 처리
  const handleAdditionalInfoRequired = () => {
    setShowAdditionalInfoInputModal(true);
  };
  
  useEffect(() => {
    // 경매 종료 이후에는 추가 bid-history 요청을 보내지 않음
    if (isAuctionEnded) {
      devLog('📚 [LiveBid] 경매 종료 상태 - 추가 입찰 내역 조회 스킵');
      return;
    }

    // 경매 데이터가 있고, 유효한 lots가 있을 때만 입찰 내역 조회
    if (auctionData && auctionData.lots && auctionData.lots.length > 0 && currentLotIndex) {
      const currentLot = getCurrentLot(auctionData.lots);
      
      if (!currentLot) {
        devWarn('📚 [LiveBid] 현재 표시할 lot을 찾을 수 없음');
        return;
      }
      
      // lot이 변경되었는지 확인 (ended가 true가 되어 다음 lot으로 넘어간 경우)
      const isLotChanged = currentDisplayedLotIndex.current !== currentLotIndex;
      
      devLog('🔍 [LiveBid] 입찰 내역 요청 조건 체크:', {
        hasAuctionData: !!auctionData,
        lotsCount: auctionData.lots.length,
        currentLotIndex,
        currentLotNo: currentLot?.lotNo,
        currentDisplayedLotIndex: currentDisplayedLotIndex.current,
        isLotChanged,
        hasRequestedBidHistory: hasRequestedBidHistory.current,
        lastRequestedLotIndex: lastRequestedLotIndex.current
      });
      
      if (isLotChanged) {
        devLog('🔄 [LiveBid] LOT 변경 감지! 이전:', currentDisplayedLotIndex.current, '→ 현재:', currentLotIndex);

        // 첫 로드(이전 lot 없음)에서는 토스트를 띄우지 않음
        // 라이브 진행 중 lot이 변경되면 사용자에게 안내
        const isInitialLotSet = currentDisplayedLotIndex.current === null;
        if (!isInitialLotSet) {
          toast.info(
            lang === 'ko'
              ? 'Lot 경매가 종료되어 다음 Lot로 넘어갑니다'
              : 'The lot auction has ended. Moving to the next lot.'
          );
        }

        // lot이 변경되었으므로 입찰 내역을 새로 불러와야 함
        hasRequestedBidHistory.current = false;
        lastRequestedLotIndex.current = null;
        currentDisplayedLotIndex.current = currentLotIndex;
        
        // 기존 입찰 내역 클리어 (새로운 lot의 입찰 내역을 깨끗하게 보여주기 위해)
        devLog('�️ [LiveBid] 기존 입찰 내역 클리어 (새 LOT으로 전환)');
        // clearBidHistory(); // 주석 처리: 입찰 내역은 유지하되 새로운 데이터로 교체
      }
      
      // 이미 같은 lotIndex로 요청했으면 스킵 (중복 호출 방지)
      if (hasRequestedBidHistory.current && lastRequestedLotIndex.current === currentLotIndex && !isLotChanged) {
        devLog('📚 [LiveBid] 입찰 내역 조회 스킵 (이미 요청함):', {
          auctionIndex,
          currentLotIndex,
          currentLotNo: currentLot?.lotNo,
          lastRequestedLotIndex: lastRequestedLotIndex.current,
          isLotChanged
        });
        return;
      }
      
      devLog('📚 [LiveBid] 입찰 내역 조회 (현재 표시되는 lot):', {
        auctionIndex,
        currentLotIndex,
        currentLotNo: currentLot?.lotNo,
        currentLotEnded: currentLot?.ended,
        lastRequestedLotIndex: lastRequestedLotIndex.current,
        isLotChanged,
        reason: isLotChanged ? 'LOT 변경됨' : '초기 로딩'
      });
      
      hasRequestedBidHistory.current = true;
      lastRequestedLotIndex.current = currentLotIndex;
      
      // lot 변경된 경우 약간의 지연 후 요청 (서버에서 처리가 완료된 후)
      const delay = isLotChanged ? 200 : 0;
      setTimeout(() => {
        getBidHistoryRef.current(auctionIndex, currentLotIndex);
      }, delay);
    } else if (auctionData) {
      devWarn('⚠️ 경매 데이터는 있지만 lots가 없거나 currentLotIndex가 없음:', {
        hasAuctionData: !!auctionData,
        lotsCount: auctionData?.lots?.length || 0,
        currentLotIndex
      });
    }
    
    // 경매 데이터가 없어지면 플래그 리셋
    if (!auctionData) {
      hasRequestedBidHistory.current = false;
      lastRequestedLotIndex.current = null;
      currentDisplayedLotIndex.current = null;
    }
  }, [auctionData, auctionIndex, currentLotIndex, getCurrentLot, lang, currentLot, isAuctionEnded]); // currentLotIndex 의존성 추가로 lot 변경시 자동 감지

  // 모든 lot이 종료되었을 때 모달 표시
  useEffect(() => {
    if (allLotsEnded && !showAuctionEndModal) {
      // 모든 lot 종료 시점부터는 마지막 lot를 고정해서 보여준다
      freezeLotAfterAuctionEndRef.current = true;
      devLog('🏁 [LiveBid] 모든 경매 아이템 입찰 종료 - 모달 표시');
      setShowAuctionEndModal(true);
    }
    // 다른 경매로 이동하거나 lots 상태가 바뀌어 다시 진행 중이 되면 해제
    if (!allLotsEnded) {
      freezeLotAfterAuctionEndRef.current = false;
    }
  }, [allLotsEnded, showAuctionEndModal]);

  // 이미지 URL 처리 함수 (PreBid와 동일)
  const getImageSrc = useCallback((imageUrl?: string | null) => {
    if (!imageUrl || imageUrl.trim() === "") {
      return "/common/nodata.svg";
    }
    
    // 이미 완전한 URL인 경우 (http, https로 시작하거나 blob: URL)
    if (imageUrl.startsWith("http") || imageUrl.startsWith("blob:")) {
      return imageUrl;
    }
    
    // 절대 경로인 경우 (/로 시작)
    if (imageUrl.startsWith("/")) {
      return `${process.env.REACT_APP_API_HOST}${imageUrl}`;
    }
     
    // 단순 파일명인 경우 (서버에서 받은 파일명)
    return `${process.env.REACT_APP_API_HOST}/${imageUrl}`;
  }, []);

  const currentLotForRender = useMemo(() => {
    if (!auctionData?.lots || auctionData.lots.length === 0) return null;
    return getCurrentLot(auctionData.lots);
  }, [auctionData?.lots, getCurrentLot]);

  // ImageCarousel에 전달할 이미지 데이터 준비
  const imageData = useMemo(() => {
    if (currentLotForRender?.images && currentLotForRender.images.length > 0) {
      return currentLotForRender.images.map((imageUrl: string) => getImageSrc(imageUrl));
    }
    return [];
  }, [currentLotForRender, getImageSrc]);

  const effectiveLikedUserIndexes = useMemo(() => {
    const lotIndex = currentLotForRender?.index;
    if (!lotIndex) return currentLotForRender?.likedUserIndexes;
    return likedUserIndexesByLot[lotIndex] ?? currentLotForRender?.likedUserIndexes;
  }, [currentLotForRender, likedUserIndexesByLot]);

  const handleFavoriteUpdate = useCallback((newLikedUserIndexes: number[]) => {
    const lotIndex = currentLotForRender?.index;
    if (!lotIndex) return;
    setLikedUserIndexesByLot((prev) => ({
      ...prev,
      [lotIndex]: newLikedUserIndexes
    }));
  }, [currentLotForRender?.index]);

  // AuctionInfo에 전달할 데이터 변환 (소켓 데이터만 사용)
  const auctionDisplayData = useMemo(() => {
    if (!auctionData) {
      devWarn('🏛️ 소켓 경매 데이터 없음');
      return undefined;
    }

    // 소켓 currentLot로 매칭된 lot만 사용 (없으면 표시용 데이터도 만들지 않음)
    const currentLot = getCurrentLot(auctionData.lots || []);
    if (!currentLot) {
      return undefined;
    }

    // 중요: auctionData.preBids는 경매(auction) 단위로 여러 lot의 preBid가 섞여 내려올 수 있음.
    // 따라서 현재 표시 중인 lot에 해당하는 preBid만 필터링해서 최고가를 사용해야 함.
    const targetLotIndexForPreBids = currentLot?.index;
    const currentLotPreBids = Array.isArray((auctionData as any).preBids)
      ? (auctionData as any).preBids.filter((bid: any) => {
          const bidLotIndex = bid?.lotIndex ?? bid?.lot;
          return targetLotIndexForPreBids !== undefined && bidLotIndex === targetLotIndexForPreBids;
        })
      : [];
    const liveStartPriceForCurrentLot = currentLotPreBids.length > 0
      ? Math.max(...currentLotPreBids.map((bid: any) => Number(bid?.price) || 0))
      : currentLot?.startPrice || 0;
    
    
    return {
      auctionHouse: auctionData.auctioneer?.name || '경매사명',
      auctioneerIndex: auctionData.auctioneer?.index || 0, // auctioneerIndex 추가
      auction: auctionData.name || '경매명',
      lotName: currentLot?.lotName || '작품명',
      lotNo: currentLot?.lotNo || 1,
      code: currentLot?.lotCode || `LOT.${currentLot?.lotNo || 1}`, // 서버 제공 lotCode 우선 사용
      index: currentLot?.index || 0, // 첫 번째 lot의 index 추가
      category: currentLot?.category?.krName || currentLot?.category?.name || '카테고리',
      subCategory: currentLot?.subCategory?.krName || currentLot?.subCategory?.name || '서브카테고리',
      liveStartTime: auctionData.auctionStartAt,
      liveEndTime: auctionData.auctionEndAt,
      preBidStartTime: auctionData.preBidStartAt,
      preBidEndTime: auctionData.preBidEndAt,
      paymentDueAt: auctionData.paymentDueAt,
      currentPrice: auctionData.currentPrice || currentLot?.startPrice,
      startPrice: currentLot?.startPrice || 0,
      startPriceUsd: currentLot?.startPriceUsd || 0,
      liveStartPrice: liveStartPriceForCurrentLot,
      exchangeRate: currentLot?.exchangeRate || 1,
      participants: auctionData.participants || 0,
      status: auctionData.status,
      auctionFeeRate: auctionData.auctionFeeRate || 0,
      platformFeeRate: auctionData.platformFeeRate || 0,
      biddingRanges: auctionData.biddingRanges || [],
      images: currentLot?.images || [],
      likeCount: currentLot?.likeCount || 0,
      isLiked: user?.index ? currentLot?.likedUserIndexes?.includes(user.index) || false : false,
      url: auctionData.url,
      description: auctionData.description,
      auctioneerInfo: {
        name: auctionData.auctioneer?.name,
        description: auctionData.auctioneer?.description,
        profilePic: auctionData.auctioneer?.profile_pic,
        term: auctionData.auctioneer?.term
      }
    };
  }, [auctionData, user?.index, getCurrentLot]);

  // 소켓 상태를 변화 감지
  useEffect(() => {
    socketStateRef.current.isConnected = isConnected;
    socketStateRef.current.auctionData = auctionData;
  }, [isConnected, auctionData]);

  const handleWatchLive = useCallback(() => {
    if (!auctionData?.url) {
      devWarn('🔗 [LiveBid] 라이브 URL이 없습니다:', auctionData);
      alert('라이브 스트림 URL을 불러올 수 없습니다.');
      return;
    }

    const url = auctionData.url;

    // Flutter WebView 환경: WebView 내부에서 해당 URL로 전체 페이지 이동
    try {
      const isFlutterApp = (window as any).isFlutterApp;
      if (isFlutterApp) {
        window.location.href = url;
        return;
      }
    } catch (e) {
      // 브리지 체크 중 오류가 나도 웹 브라우저용 새창 로직으로 폴백
      devWarn('🔗 [LiveBid] Flutter 브리지 확인 중 오류:', e);
    }

    // 일반 웹 브라우저: 새 탭/새 창으로 열기
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [auctionData]);

  const toggleLotDetail = useCallback(() => {
    setIsLotDetailCollapsed(!isLotDetailCollapsed);
  }, [isLotDetailCollapsed]);

  const handleAuctionEndConfirm = useCallback(() => {
    devLog('🏠 [LiveBid] 경매 종료 - 메인 페이지로 이동');
    navigate('/', { replace: true }); // replace로 뒤로가기 방지
  }, [navigate]);

  // 입찰 내역을 BidHistory 컴포넌트에서 사용할 수 있는 형태로 변환
  // - 라이브 입찰(bidHistory) + 사전입찰(auctionData.preBids)을 하나의 리스트로 합침
  const transformedBidHistory = useMemo(() => {
    // 현재 사용자 index (Context 우선, 없으면 localStorage에서 복원)
    let currentUserIndex: number | null = null;
    if (user?.index) {
      currentUserIndex = user.index;
    } else {
      try {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          const parsed = JSON.parse(storedUser);
          if (typeof parsed.index === 'number') {
            currentUserIndex = parsed.index;
          }
        }
      } catch {
        // ignore parse error
      }
    }

    const merged: { bidNumber: string; amount: number; isMyBid: boolean }[] = [];

    // 1) 라이브 입찰 내역 (소켓 bidHistory)
    if (Array.isArray(bidHistory)) {
      bidHistory.forEach((bid) => {
        const isMyBid = currentUserIndex && bid.user?.index
          ? currentUserIndex === bid.user.index
          : false;
        merged.push({
          bidNumber: bid.code,
          amount: bid.price,
          isMyBid
        });
      });
    }

    // 2) 사전입찰 내역 (auctionData.preBids) - 현재 LOT에 해당하는 것만
    if (auctionData && Array.isArray((auctionData as any).preBids) && currentLotIndex) {
      const preBidLabel = lang === 'ko' ? '사전입찰' : 'Pre-bid';

      (auctionData as any).preBids
        .filter((preBid: any) => {
          const bidLotIndex = preBid?.lotIndex ?? preBid?.lot;
          return bidLotIndex === currentLotIndex;
        })
        .forEach((preBid: any) => {
          const isMyBid = currentUserIndex && preBid.user?.index
            ? currentUserIndex === preBid.user.index
            : false;
          merged.push({
            bidNumber: preBid.anonymousCode || preBid.code || preBidLabel,
            amount: preBid.price,
            isMyBid
          });
        });
    }

    // 3) 금액 기준 내림차순 정렬 (최고가가 위로 오도록)
    merged.sort((a, b) => b.amount - a.amount);

    return merged;
  }, [auctionData, bidHistory, currentLotIndex, lang, user?.index]);

  // 현재 로그인 사용자 index (Context 우선, 없으면 localStorage에서 복원)
  const currentUserIndex = useMemo(() => {
    if (user?.index) return user.index;

    try {
      const storedUser = localStorage.getItem('user');
      if (!storedUser) return null;
      const parsed = JSON.parse(storedUser);
      return typeof parsed.index === 'number' ? parsed.index : null;
    } catch {
      return null;
    }
  }, [user?.index]);

  // 내 입찰 정보 가져오기 (현재 사용자의 가장 최근 입찰 - 라이브 입찰 우선, 없으면 사전입찰 사용)
  const myBidInfo = useMemo(() => {
    // 기본 환율은 현재 LOT의 환율 또는 1300
    let exchangeRate = 1300;
    if (auctionData?.lots && auctionData.lots.length > 0) {
      const currentLotForRate = getCurrentLot(auctionData.lots);
      if (currentLotForRate?.exchangeRate) {
        exchangeRate = currentLotForRate.exchangeRate;
      }
    }

    // 사전입찰 레이블 (서버에 코드가 없을 때 사용)
    const preBidLabel = lang === 'ko' ? '사전입찰' : 'Pre-bid';

    if (!currentUserIndex) {
      return { hasBid: false, exchangeRate };
    }

    // 1) 라이브 입찰 내역에서 현재 사용자의 가장 최근 입찰 찾기
    if (bidHistory.length > 0) {
      const myLiveBids = bidHistory.filter(bid => bid.user?.index && bid.user.index === currentUserIndex);
      if (myLiveBids.length > 0) {
        const latestMyLiveBid = myLiveBids[0]; // 이미 정렬되어 있다고 가정
        return {
          bidNumber: latestMyLiveBid.code,
          bidAmount: latestMyLiveBid.price,
          hasBid: true,
          exchangeRate
        };
      }
    }

    // 2) 라이브 입찰이 없다면, 사전입찰(preBids)에서 현재 LOT + 현재 사용자 조건에 맞는 입찰 사용
    if (auctionData && Array.isArray((auctionData as any).preBids) && currentLotIndex) {
      const currentLotPreBidsForUser = (auctionData as any).preBids.filter((preBid: any) => {
        const bidLotIndex = preBid?.lotIndex ?? preBid?.lot;
        const bidUserIndex = preBid?.user?.index;
        return bidLotIndex === currentLotIndex && bidUserIndex === currentUserIndex;
      });

      if (currentLotPreBidsForUser.length > 0) {
        // 가장 최근 사전입찰을 사용 (createAt 기준, 없으면 배열 첫 번째)
        const latestPreBid = currentLotPreBidsForUser.reduce((latest: any, bid: any) => {
          if (!latest) return bid;
          if (!latest.createAt || !bid.createAt) return latest;
          return bid.createAt > latest.createAt ? bid : latest;
        }, currentLotPreBidsForUser[0]);

        return {
          // preBid에 anonymousCode 우선, 없으면 code/레이블 폴백
          bidNumber: latestPreBid.anonymousCode || latestPreBid.code || preBidLabel,
          bidAmount: latestPreBid.price,
          hasBid: true,
          exchangeRate
        };
      }
    }

    return {
      hasBid: false,
      exchangeRate
    };
  }, [auctionData, bidHistory, currentUserIndex, currentLotIndex, getCurrentLot, lang]);

  // 최소 입찰가 계산 함수 (LiveBidRequest와 동일한 로직)
  const calculateMinimumBidPrice = useCallback(() => {
    if (!auctionData?.biddingRanges || !auctionData?.lots || auctionData.lots.length === 0) {
      return null;
    }

    const currentLot = getCurrentLot(auctionData.lots);
    if (!currentLot) return null;

    // 숫자 검증 함수
    const safeNumber = (value: any, defaultValue: number = 0): number => {
      const num = Number(value);
      return isNaN(num) || !isFinite(num) ? defaultValue : num;
    };

    // 라이브 시작가 계산
    // 중요: auctionData.preBids는 경매(auction) 단위로 여러 lot의 preBid가 섞여 내려올 수 있음.
    const targetLotIndexForPreBids = currentLot.index || lotIndex;
    const currentLotPreBids = Array.isArray((auctionData as any).preBids)
      ? (auctionData as any).preBids.filter((bid: any) => {
          const bidLotIndex = bid?.lotIndex ?? bid?.lot;
          return bidLotIndex === targetLotIndexForPreBids;
        })
      : [];

    const hasPreBids = currentLotPreBids.length > 0;

    const liveStartPrice = hasPreBids
      ? Math.max(...currentLotPreBids.map((bid: any) => safeNumber(bid?.price, 0)))
      : safeNumber(currentLot.startPrice, 0);
    
    let maxBidPrice = liveStartPrice; // 기본값은 라이브 시작가
    let hasLiveBids = false;

    // bidHistory에서 현재 로트에 해당하는 가장 높은 입찰가 찾기
    if (bidHistory && bidHistory.length > 0) {
      const targetLotIndex = currentLot.index || lotIndex;
      
      // 현재 로트에 해당하는 입찰들만 필터링
      const currentLotBids = bidHistory.filter(bid => bid.lot === targetLotIndex);
      
      if (currentLotBids.length > 0) {
        // 가장 높은 입찰가 찾기
        const highestBid = currentLotBids.reduce((max, bid) => {
          const bidPrice = safeNumber(bid.price, 0);
          return bidPrice > max ? bidPrice : max;
        }, 0);
        
        if (highestBid > 0) {
          maxBidPrice = highestBid;
          hasLiveBids = true;
        }
      }
    }

    // maxBidPrice가 유효하지 않으면 기본값 사용
    if (!isFinite(maxBidPrice) || maxBidPrice <= 0) {
      maxBidPrice = 100000; // 기본 시작가 10만원
    }

    // 해당 금액이 속하는 구간 찾기
    const biddingRanges = auctionData.biddingRanges;
    let currentRange = biddingRanges.find((range: BiddingRange) => {
      const minAmount = safeNumber(range.minBidAmount, 0);
      const maxAmount = range.maxBidAmount !== null ? safeNumber(range.maxBidAmount, 0) : null;
      
      return maxBidPrice >= minAmount && (maxAmount === null || maxBidPrice < maxAmount);
    });

    // 구간을 찾지 못했거나 maxBidPrice가 구간의 최대값과 같은 경우, 다음 구간을 찾기
    if (!currentRange) {
      // maxBidPrice가 어떤 구간의 maxBidAmount와 정확히 일치하는 경우 다음 구간 찾기
      const rangeIndex = biddingRanges.findIndex((range: BiddingRange) => 
        range.maxBidAmount !== null && maxBidPrice === safeNumber(range.maxBidAmount, 0)
      );
      
      if (rangeIndex !== -1 && rangeIndex + 1 < biddingRanges.length) {
        // 다음 구간이 존재하면 사용
        currentRange = biddingRanges[rangeIndex + 1];
      } else {
        // 마지막 구간이거나 구간을 찾을 수 없는 경우
        currentRange = biddingRanges[biddingRanges.length - 1];
      }
    }

    if (!currentRange) {
      // 방어적 코딩: 가장 마지막 구간을 사용
      currentRange = biddingRanges[biddingRanges.length - 1];
      
      if (!currentRange) {
        // 구간이 아예 없는 경우 기본값 반환
        return maxBidPrice + 1000;
      }
    }

    // 최소 입찰가 계산 (rate를 증액 단위로 사용)
    const increment = safeNumber(currentRange.rate, 1000);
    const noAnyBids = !hasPreBids && !hasLiveBids;

    // preBid도 없고 라이브 입찰도 전혀 없는 경우: 시작가는 그대로 사용
    // 그 외의 경우(사전입찰 또는 라이브 입찰 이력이 있는 경우): 최고가 + increment
    const minimumBidPrice = noAnyBids ? maxBidPrice : maxBidPrice + increment;

    return minimumBidPrice;
  }, [auctionData, bidHistory, lotIndex, getCurrentLot]);

  // 최소 입찰가 (메모이제이션)
  const minimumBidPrice = useMemo(() => {
    return calculateMinimumBidPrice();
  }, [calculateMinimumBidPrice]);

  // 현재가 계산 (최고 입찰가 또는 라이브 시작가)
  const currentPrice = useMemo(() => {
    if (!auctionData?.lots || auctionData.lots.length === 0) return 1000000;
    
    const currentLot = getCurrentLot(auctionData.lots);
    if (!currentLot) return 1000000;

    // 입찰 내역에서 현재 로트의 최고가 찾기
    if (bidHistory && bidHistory.length > 0) {
      const currentLotBids = bidHistory.filter(bid => bid.lot === currentLot.index);
      if (currentLotBids.length > 0) {
        const highestBid = Math.max(...currentLotBids.map(bid => bid.price));
        return highestBid;
      }
    }
    
    // 입찰이 없으면 라이브 시작가 반환
    // 중요: auctionData.preBids는 경매(auction) 단위로 여러 lot의 preBid가 섞여 내려올 수 있음.
    const targetLotIndexForPreBids = currentLot.index;
    const currentLotPreBids = Array.isArray((auctionData as any).preBids)
      ? (auctionData as any).preBids.filter((bid: any) => {
          const bidLotIndex = bid?.lotIndex ?? bid?.lot;
          return bidLotIndex === targetLotIndexForPreBids;
        })
      : [];
    const liveStartPrice = currentLotPreBids.length > 0
      ? Math.max(...currentLotPreBids.map((bid: any) => Number(bid?.price) || 0))
      : currentLot.startPrice || 1000000;
    
    // 경매 전체(currentPrice)가 아니라, 현재 lot 기준의 가격만 사용
    return liveStartPrice;
  }, [auctionData, bidHistory, getCurrentLot]);

  // 버튼 텍스트 (통화 코드 포함 + 입찰)
  const bidButtonText = useMemo(() => {
    if (!minimumBidPrice) {
      return texts.bidButton; // 기본 버튼 텍스트
    }

    // 언어별 통화 표시 (소켓에서 받은 환율 정보 사용)
    const currentLot = auctionData?.lots ? getCurrentLot(auctionData.lots) : null;
    const exchangeRate = currentLot?.exchangeRate || 1409.09;

    if (lang === 'ko') {
      return `KRW ${minimumBidPrice.toLocaleString()} ${texts.bid}`;
    } else {
      const usdPrice = Math.round((minimumBidPrice / exchangeRate) * 100) / 100;
      return `USD ${usdPrice.toFixed(2)} ${texts.bid}`;
    }
  }, [minimumBidPrice, texts.bidButton, texts.bid, lang, auctionData, getCurrentLot]);

  const isHighestBidder = useMemo(() => {
    if (!Array.isArray(transformedBidHistory) || transformedBidHistory.length === 0) {
      return false;
    }

    const maxAmount = transformedBidHistory.reduce((max, bid) => {
      return bid.amount > max ? bid.amount : max;
    }, 0);

    return transformedBidHistory.some((bid) => bid.isMyBid && bid.amount === maxAmount);
  }, [transformedBidHistory]);

  const isHighestBidderForUi = isHighestBidder;

  const handleBidRequest = useCallback(async () => {
    if (!user) {
      setShowLoginModal(true);
      return;
    }

    const isNameEmpty = !user.name || user.name.trim() === '';
    const isLocationEmpty = !user.locationType || user.locationType.trim() === '';
    if (isNameEmpty || isLocationEmpty || user.needMoreInfo) {
      setShowAdditionalInfoInputModal(true);
      return;
    }

    if (isBanned) {
      setShowBidRestrictedModal(true);
      return;
    }

    const currentIsConnected = socketStateRef.current.isConnected;
    const currentAuctionData = socketStateRef.current.auctionData;

    if (!currentIsConnected) {
      toast.error(texts.socketRequired);
      return;
    }

    if (!currentAuctionData) {
      toast.error(texts.auctionLoading);
      return;
    }

    const currentLot = getCurrentLot(currentAuctionData.lots || []);
    const currentLotIndex = currentLot?.index;

    if (!currentLotIndex) {
      toast.error(texts.currentLotMissing);
      return;
    }

    if (isAgreementStatusLoading || isAuctionAgreed === null) {
      toast.warning(texts.agreementStatusLoading);
      return;
    }

    if (!isAuctionAgreed) {
      navigate('/live-bid-request', {
        state: {
          auctionData: currentAuctionData,
          auctionIndex,
          lotIndex: currentLotIndex
        }
      });
      return;
    }

    const actualPrice = minimumBidPrice;

    if (!actualPrice) {
      toast.error(texts.invalidBidPrice);
      return;
    }

    if (bidInProgressRef.current) {
      const now = Date.now();
      if (!lastBidWarningAtRef.current || now - lastBidWarningAtRef.current > 2000) {
        lastBidWarningAtRef.current = now;
        toast.warning(texts.bidInProgress);
      }
      return;
    }

    if (isSubmittingBid) {
      const now = Date.now();
      if (!lastBidWarningAtRef.current || now - lastBidWarningAtRef.current > 2000) {
        lastBidWarningAtRef.current = now;
        toast.warning(texts.bidInProgress);
      }
      return;
    }

    const bidRequestKey = `${auctionIndex}-${currentLotIndex}-${actualPrice}`;
    const nowForDuplicateCheck = Date.now();
    if (
      lastBidRequestId === bidRequestKey &&
      bidSubmitTime !== null &&
      nowForDuplicateCheck - bidSubmitTime < 1000
    ) {
      toast.warning(texts.bidInProgress);
      return;
    }

    try {
      bidInProgressRef.current = true;
      setIsSubmittingBid(true);
      const now = Date.now();
      setBidSubmitTime(now);
      setLastBidRequestId(bidRequestKey);
      bidTargetLotIndexRef.current = currentLotIndex;
      lastRequestedPriceRef.current = actualPrice;
      placeBid(auctionIndex, currentLotIndex, actualPrice);
      toast.info(texts.bidSubmitting);
    } catch (error) {
      bidInProgressRef.current = false;
      setIsSubmittingBid(false);
      setBidSubmitTime(null);
      setLastBidRequestId(null);
      bidTargetLotIndexRef.current = null;
      lastRequestedPriceRef.current = null;
      devWarn('⚠️ [LiveBid] 즉시 입찰 요청 실패:', error);
      toast.error(texts.invalidBidPrice);
    }
  }, [navigate, auctionIndex, getCurrentLot, user, isBanned, isAgreementStatusLoading, isAuctionAgreed, minimumBidPrice, isSubmittingBid, bidSubmitTime, lastBidRequestId, placeBid, texts]);

  // 입찰 성공 감지 - LiveBidRequest와 동일한 패턴
  useEffect(() => {
    if (bidHistory.length > 0 && isSubmittingBid && bidSubmitTime) {
      const latestBid = bidHistory[0];
      const targetLotIndex = bidTargetLotIndexRef.current;
      const bidTime = Date.now();
      const isRecentBid = bidTime - bidSubmitTime < 10000;
      const isTargetLot = targetLotIndex !== null && latestBid.lot === targetLotIndex;
      const isMyBid = user?.index && latestBid.user?.index
        ? latestBid.user.index === user.index
        : false;
      const matchesRequestedPrice = lastRequestedPriceRef.current != null
        ? latestBid.price === lastRequestedPriceRef.current
        : false;
      const isSuccessMatch = isRecentBid && isTargetLot && (isMyBid || matchesRequestedPrice);

      if (isSuccessMatch) {
        bidInProgressRef.current = false;
        setIsSubmittingBid(false);
        setBidSubmitTime(null);
        setLastBidRequestId(null);
        bidTargetLotIndexRef.current = null;
        lastRequestedPriceRef.current = null;
        toast.success(`입찰이 성공적으로 접수되었습니다! (${latestBid.code || 'N/A'})`);
      }
    }
  }, [bidHistory, isSubmittingBid, bidSubmitTime, user?.index]);

  // 입찰 타임아웃 처리
  useEffect(() => {
    if (!isSubmittingBid) return;

    const timeout = window.setTimeout(() => {
      bidInProgressRef.current = false;
      setIsSubmittingBid(false);
      setBidSubmitTime(null);
      setLastBidRequestId(null);
      bidTargetLotIndexRef.current = null;
      lastRequestedPriceRef.current = null;
      toast.error(lang === 'ko'
        ? '입찰 요청 시간이 초과되었습니다. 다시 시도해주세요.'
        : 'Bid request timed out. Please try again.');
    }, 15000);

    return () => window.clearTimeout(timeout);
  }, [isSubmittingBid, lang]);

  const handleStartPress = useCallback(() => {
    const currentAuctionData = socketStateRef.current.auctionData;
    const currentLotData = currentAuctionData ? getCurrentLot(currentAuctionData.lots || []) : null;
    bidTargetLotIndexRef.current = currentLotData?.index ?? null;
  }, [getCurrentLot]);

  const handleIncomplete = useCallback(() => {
    devLog('🎯 [LiveBid] 입찰 버튼 누르기 완료되지 않음');
  }, []);

  const handleDisabled = useCallback(() => {
    return;
  }, []);

  // 입찰 버튼 props를 메모이제이션 - 소켓 상태와 독립적
  const bidButtonProps = useMemo(() => ({
    onClick: handleBidRequest,
    buttonText: bidButtonText
  }), [handleBidRequest, bidButtonText]);

  // 연결 상태 텍스트 (현재 UI에서 사용하지 않음)
  // const connectionStatusText = useMemo(() => {
  //   if (isConnected) return texts.connected;
  //   return texts.connecting;
  // }, [isConnected, texts.connected, texts.connecting]);

  // 필수 파라미터가 없으면 에러 화면 표시
  if (!auctionIndex) {
    return (
      <MainLayout currentPage="home">
        <CenteredLayout>
          <ProjectAppbar 
            currentPage="home"
            title={texts.title}
            showBackButton={true}
            onBack={() => navigate(-1)}
          />
          <Container>
            <ContentContainer>
              <ErrorMessage>
                <span>{texts.missingParams}</span>
                <PrimaryButton onClick={() => navigate(-1)}>
                  {texts.goBack}
                </PrimaryButton>
              </ErrorMessage>
            </ContentContainer>
          </Container>
        </CenteredLayout>
      </MainLayout>
    );
  }

  const MobileLayout = (
    <MainLayout currentPage="home">
      <CenteredLayout>
        <ProjectAppbar 
          currentPage="home"
          title={texts.title}
          showBackButton={true}
          onBack={() => navigate(-1)}
        />
        <Container>
          <ContentContainer>
            {/* 연결 상태 표시 */}
            {/* <MemoizedConnectionStatus 
              isConnected={isConnected}
              statusText={connectionStatusText}
            /> */}

            {/* 에러 메시지 표시 */}
            {/* {error && (
              <ErrorMessage>
                <span>{error}</span>
                <ErrorCloseButton onClick={clearError}>×</ErrorCloseButton>
              </ErrorMessage>
            )} */}

            <MemoizedTitleRow 
              title={texts.liveAuction}
              onWatchLive={handleWatchLive}
              watchLiveText={texts.watchLive}
            />
            {auctionData?.url && (
              <YouTubePlayer youtubeUrl={auctionData.url}>
                {/* <BidOverlay bids={transformedBidHistory} /> */}
              </YouTubePlayer>
            )}
            
            {/* LOT 상세 섹션 */}
            <LotDetailHeader onClick={toggleLotDetail}>
              <FormTitle>{texts.lotDetail}</FormTitle>
              <ToggleButton>
                {isLotDetailCollapsed ? texts.showMore : texts.showLess}
              </ToggleButton>
            </LotDetailHeader>
            
            <CollapsibleContent $isCollapsed={isLotDetailCollapsed}>
              {!auctionData || !currentLotForRender ? (
                <LoadingMessage>
                  {!auctionData ? texts.noAuctionData : (lang === 'ko' ? '현재 LOT 정보를 불러오는 중입니다.' : 'Loading current lot information...')}
                </LoadingMessage>
              ) : (
                <>
                  <ImageCarousel 
                    images={imageData}
                    lotId={currentLotForRender?.index}
                    likedUserIndexes={effectiveLikedUserIndexes}
                    onFavoriteUpdate={handleFavoriteUpdate}
                    onLoginRequired={() => setShowLoginModal(true)}
                    onAdditionalInfoRequired={handleAdditionalInfoRequired}
                  />
                  <AuctionInfo 
                    auctionData={auctionDisplayData} 
                    showLiveStartPrice={true}
                  />
                  <LotDescription 
                    isVisible={!isLotDetailCollapsed} 
                    description={(() => {
                      if (!auctionData?.lots || auctionData.lots.length === 0) return '';
                      const currentLot = getCurrentLot(auctionData.lots);
                      return currentLot?.description || '';
                    })()} 
                  />
                </>
              )}
            </CollapsibleContent>
         
            <div style={{ marginTop: '24px' }}>
              <AuctionTimer 
                currentPrice={currentPrice}
                exchangeRate={(() => {
                  if (!auctionData?.lots || auctionData.lots.length === 0) return 1300;
                  const currentLot = getCurrentLot(auctionData.lots);
                  return currentLot?.exchangeRate || 1300;
                })()}
                countdownStatus={countdownStatus} // 소켓에서 받은 카운트다운 상태 전달
                onTimerClick={() => devLog('Timer clicked')}
              />
            </div>
            <div style={{ marginTop: '16px' }}>
              <MyBid {...myBidInfo} />
            </div>
            <div style={{ marginTop: '16px' }}>
              <BidHistory bidHistory={transformedBidHistory} />
            </div>
          </ContentContainer>
          
          {/* 고정 버튼 영역 - 모바일 */}
          <MemoizedFixedButtonContainer
            onClick={bidButtonProps.onClick}
            buttonText={bidButtonProps.buttonText}
            isAgreed={isAuctionAgreed}
            isSubmitting={isSubmittingBid}
            isHighestBidder={isHighestBidderForUi}
            tooltipText={texts.bidButtonTooltip}
            waitingTooltipText={texts.waitingNextBidderTooltip}
            animatedButtonRef={animatedButtonRef}
            onStartPress={handleStartPress}
            onIncomplete={handleIncomplete}
            disabledCallback={handleDisabled}
          />
        </Container>
      </CenteredLayout>
    </MainLayout>
  );

  const DesktopLayout = (
    <MainLayout currentPage="home">
      <CenteredLayout>
        <ProjectAppbar 
          currentPage="home"
          title={texts.title}
          showBackButton={true}
          onBack={() => navigate(-1)}
        />
        <Container>
          <ContentContainer>

            {/* LOT 상세 섹션 - 웹에서는 맨 위에 배치, 항상 표시 */}
            <DesktopLotTitle>{texts.lotDetail}</DesktopLotTitle>
            
            {!auctionData || !currentLotForRender ? (
              <LoadingMessage>
                {!auctionData ? texts.noAuctionData : (lang === 'ko' ? '현재 LOT 정보를 불러오는 중입니다.' : 'Loading current lot information...')}
              </LoadingMessage>
            ) : (
              <DesktopLotContent>
                <ImageCarouselWrapper>
                  <ImageCarousel 
                    images={imageData}
                    lotId={currentLotForRender?.index}
                    likedUserIndexes={effectiveLikedUserIndexes}
                    onFavoriteUpdate={handleFavoriteUpdate}
                    onLoginRequired={() => setShowLoginModal(true)}
                    onAdditionalInfoRequired={handleAdditionalInfoRequired}
                  />
                </ImageCarouselWrapper>
                <AuctionInfo 
                  auctionData={auctionDisplayData} 
                  showLiveStartPrice={true}
                />
              </DesktopLotContent>
            )}
            
            <MemoizedTitleRow 
              title={texts.liveAuction}
              onWatchLive={handleWatchLive}
              watchLiveText={texts.watchLive}
            />
            {auctionData?.url && (
              <YouTubePlayer youtubeUrl={auctionData.url}>
                {/* <BidOverlay bids={transformedBidHistory} /> */}
              </YouTubePlayer>
            )}
            <LotDescription description={(() => {
              if (!auctionData?.lots || auctionData.lots.length === 0) return '';
              const currentLot = getCurrentLot(auctionData.lots);
              return currentLot?.description || '';
            })()} />
            
            {/* 웹용 경매 카운트 */}
            <AuctionTimerWrapper>
              <AuctionTimer 
                currentPrice={currentPrice}
                exchangeRate={(() => {
                  if (!auctionData?.lots || auctionData.lots.length === 0) return 1300;
                  const currentLot = getCurrentLot(auctionData.lots);
                  return currentLot?.exchangeRate || 1300;
                })()}
                countdownStatus={countdownStatus} // 소켓에서 받은 카운트다운 상태 전달
                isWeb={true}
                onTimerClick={() => devLog('Timer clicked')}
              />
            </AuctionTimerWrapper>
            
            {/* 웹용 입찰 섹션 - 나의 입찰과 입찰 내역을 같은 행에 배치 */}
            <DesktopBidSection>
              <BidHistoryWrapper>
                <BidHistory bidHistory={transformedBidHistory} />
              </BidHistoryWrapper>
              <MyBidWrapper $hasBid={myBidInfo.hasBid}>
                <MyBid {...myBidInfo} isWeb={true} />
              </MyBidWrapper>
            </DesktopBidSection>
          </ContentContainer>
          
          {/* 고정 버튼 영역 - 데스크톱 */}
          <MemoizedFixedButtonContainer
            onClick={bidButtonProps.onClick}
            buttonText={bidButtonProps.buttonText}
            isAgreed={isAuctionAgreed}
            isSubmitting={isSubmittingBid}
            isHighestBidder={isHighestBidderForUi}
            tooltipText={texts.bidButtonTooltip}
            waitingTooltipText={texts.waitingNextBidderTooltip}
            animatedButtonRef={animatedButtonRef}
            onStartPress={handleStartPress}
            onIncomplete={handleIncomplete}
            disabledCallback={handleDisabled}
          />
        </Container>
      </CenteredLayout>
    </MainLayout>
  );

  return (
    <>
      <ResponsiveView
        mobileView={MobileLayout}
        desktopView={DesktopLayout}
      />
      
      {/* 경매 종료 모달 */}
      <AuctionEndedModal
        isOpen={showAuctionEndModal}
        onClose={handleAuctionEndConfirm}
        onConfirm={handleAuctionEndConfirm}
      />

      {/* 로그인 필요 모달 */}
      <LoginRequiredModal
        isOpen={showLoginModal}
        onClose={handleCloseLoginModal}
        onLogin={handleLoginFromModal}
      />

      {/* 추가 정보 직접 입력 모달 */}
      <AdditionalInfoInputModal
        isOpen={showAdditionalInfoInputModal}
        onClose={() => setShowAdditionalInfoInputModal(false)}
        onCompleted={() => {
          // 추가 정보 입력 완료 후, 좋아요/입찰 등 다시 시도할 수 있도록 필요 시 로직 확장
        }}
      />

      {/* 입찰 제한 모달 */}
      <BidRestrictedModal
        isOpen={showBidRestrictedModal}
        onClose={() => setShowBidRestrictedModal(false)}
      />
    </>
  );
}

export default LiveBid;
